<template>
  <v-container fluid class="pa-4">
    <div v-if="loading" class="pa-8 text-center"><v-progress-circular indeterminate /></div>
    <div v-else>
      <div class="d-flex justify-end mb-2">
        <v-tooltip location="bottom" max-width="280">
          <template #activator="{ on, attrs }">
            <v-btn
              v-bind="attrs"
              v-on="on"
              small
              text
              :to="publicLeaderboardPath"
              target="_blank"
              rel="noopener"
              color="grey darken-2"
            >
              <v-icon left small>mdi-trophy-outline</v-icon>
              Ver ranking público
            </v-btn>
          </template>
          <span>Página pública de los mejores resultados (los socios optan por aparecer).</span>
        </v-tooltip>
      </div>
      <v-row>
        <v-col v-for="kpi in kpis" :key="kpi.label" cols="12" sm="6" md="3">
          <v-card class="pa-4" outlined>
            <div class="text-caption grey--text">{{ kpi.label }}</div>
            <div class="text-h4 font-weight-bold" :style="{ color: kpi.color }">{{ kpi.value }}</div>
            <div v-if="kpi.hint" class="text-caption grey--text">{{ kpi.hint }}</div>
          </v-card>
        </v-col>
      </v-row>

      <nav class="tabs-nav mt-4">
        <button
          v-for="t in tabs"
          :key="t.value"
          class="tab-btn"
          :class="{ active: tab === t.value }"
          @click="tab = t.value"
        >
          {{ t.label }}
        </button>
      </nav>

      <div class="tab-content mt-3">
        <div v-if="tab === 'distribucion'">
          <v-card class="pa-4" outlined>
            <h3 class="text-h6 mb-2">Cantidad de socios por nivel de madurez</h3>
            <SacRadarChart :data="distribucionRadar" height="320px" />
          </v-card>
        </div>

        <div v-if="tab === 'rubros'">
          <v-row>
            <v-col cols="12" md="6">
              <v-card class="pa-4" outlined>
                <h3 class="text-h6 mb-2">Calificación promedio por rubro (0-100)</h3>
                <SacRadarChart :data="radarData" :force-max="100" height="320px" />
              </v-card>
            </v-col>
            <v-col cols="12" md="6">
              <v-card class="pa-4" outlined>
                <h3 class="text-h6 mb-2">Calificación mediana por rubro (0-100)</h3>
                <SacRadarChart :data="radarMedian" :force-max="100" height="320px" />
              </v-card>
            </v-col>
          </v-row>
        </div>

        <div v-if="tab === 'secciones'">
          <v-card class="pa-4 mb-3" outlined>
            <h3 class="text-h6 mb-2">Secciones con menor calificación promedio (%)</h3>
            <SacRadarChart :data="bottomRadar" :force-max="100" height="360px" />
          </v-card>
          <v-card class="pa-4" outlined>
            <h3 class="text-h6 mb-2">Secciones con mayor calificación promedio (%)</h3>
            <SacRadarChart :data="topRadar" :force-max="100" height="360px" />
          </v-card>
        </div>

        <div v-if="tab === 'tendencia'">
          <v-card class="pa-4" outlined>
            <h3 class="text-h6 mb-2">Tendencia mensual</h3>
            <v-data-table
              :headers="trendHeaders"
              :items="(trend && trend.series) || []"
              :items-per-page="12"
              hide-default-footer
            />
          </v-card>
        </div>

        <div v-if="tab === 'criticas'">
          <v-card class="pa-4" outlined>
            <h3 class="text-h6 mb-2">Áreas críticas</h3>
            <v-data-table
              :headers="flagHeaders"
              :items="(topFlags && topFlags.topFlags) || []"
              :items-per-page="10"
              hide-default-footer
            />
          </v-card>
        </div>

        <div v-if="tab === 'intentos'">
          <v-card class="pa-4" outlined>
            <h3 class="text-h6 mb-2">Intentos de socios</h3>
            <v-data-table
                :headers="responseHeaders"
                :items="(responses && responses.responses) || []"
                :items-per-page="20"
                :server-items-length="(responses && responses.pagination && responses.pagination.total) || 0"
                @update:options="onRespPage"
            >
                <template #[`item.user`]="{ item }">
                    {{ item.user && item.user.name }} {{ item.user && item.user.firstLastname }}
                </template>
                <template #[`item.totalScore`]="{ item }">
                    <v-chip v-if="item.totalScore != null" :color="colorForLevel(item.overallLevel)" text-color="white" small>
                        {{ Math.round(item.totalScore) }} — {{ item.overallLevel }}
                    </v-chip>
                </template>
            </v-data-table>
          </v-card>
        </div>
      </div>
    </div>
  </v-container>
</template>

<script>
import { mapMutations } from 'vuex';
import {
    getDashboardOverview,
    getDashboardByRubro,
    getDashboardBySection,
    getDashboardTrend,
    getDashboardTopFlags,
    getDashboardResponses,
} from '../../api/sac';
import SacRadarChart from '../../components/sac/SacRadarChart.vue';

export default {
    name: 'SacDashboard',
    components: { SacRadarChart },
    data() {
        return {
            loading: true,
            tab: 'distribucion',
            tabs: [
                { value: 'distribucion', label: 'Distribución por nivel' },
                { value: 'rubros', label: 'Rubros' },
                { value: 'secciones', label: 'Secciones' },
                { value: 'tendencia', label: 'Tendencia' },
                { value: 'criticas', label: 'Areas criticas' },
                { value: 'intentos', label: 'Intentos' },
            ],
            overview: null,
            rubros: null,
            sections: null,
            topFlags: null,
            trend: null,
            responses: null,
            distHeaders: [
                { text: 'Nivel', value: 'level' },
                { text: 'Cantidad', value: 'count' },
            ],
            rubroHeaders: [
                { text: 'Rubro', value: 'name' },
                { text: 'Intentos', value: 'attempts' },
                { text: 'Promedio', value: 'avg' },
                { text: 'Mediana', value: 'median' },
            ],
            sectionHeaders: [
                { text: 'Sección', value: 'name' },
                { text: 'Intentos', value: 'attempts' },
                { text: 'Promedio %', value: 'avg' },
            ],
            flagHeaders: [
                { text: 'Código', value: 'code' },
                { text: 'Pregunta', value: 'text' },
                { text: 'Intentos', value: 'attempts' },
                { text: 'Promedio %', value: 'pct' },
            ],
            trendHeaders: [
                { text: 'Mes', value: 'label' },
                { text: 'Intentos', value: 'attempts' },
                { text: 'Score promedio', value: 'avgScore' },
            ],
            responseHeaders: [
                { text: 'Socio', value: 'user' },
                { text: 'Email', value: 'user.email' },
                { text: 'Completado', value: 'completedAt' },
                { text: 'Score', value: 'totalScore' },
            ],
        };
    },
    computed: {
        publicLeaderboardPath() {
            return '/sac/leaderboard';
        },
        kpis() {
            const o = this.overview || {};
            return [
                { label: 'Intentos completados', value: o.attempts || 0, color: 'primary', hint: 'Total histórico' },
                { label: 'Socios únicos', value: o.uniqueUsers || 0, color: 'success' },
                { label: 'Score promedio', value: (o.avgScore || 0).toFixed(1), color: 'warning', hint: '0-100' },
                { label: '% ≥ Aceptable', value: `${(o.pctAboveAceptable || 0).toFixed(1)}%`, color: 'info' },
            ];
        },
        distribucionRadar() {
            const list = (this.overview && this.overview.distribution) || [];
            return {
                labels: list.map((d) => d.level),
                datasets: [{
                    label: 'Cantidad',
                    data: list.map((d) => d.count),
                    backgroundColor: 'rgba(76, 175, 80, 0.25)',
                    borderColor: '#388E3C',
                    borderWidth: 2,
                }],
            };
        },
        radarData() {
            const list = this.rubros?.rubros || [];
            return {
                labels: list.map((r) => r.name),
                datasets: [{
                    label: 'Promedio',
                    data: list.map((r) => r.avg),
                    backgroundColor: 'rgba(25, 118, 210, 0.25)',
                    borderColor: '#1976D2',
                    borderWidth: 2,
                }],
            };
        },
        radarMedian() {
            const list = this.rubros?.rubros || [];
            return {
                labels: list.map((r) => r.name),
                datasets: [{
                    label: 'Mediana',
                    data: list.map((r) => r.median),
                    backgroundColor: 'rgba(255, 152, 0, 0.25)',
                    borderColor: '#F57C00',
                    borderWidth: 2,
                }],
            };
        },
        bottomRadar() {
            const list = this.sections?.bottom || [];
            return {
                labels: list.map((s) => s.name),
                datasets: [{
                    label: 'Promedio %',
                    data: list.map((s) => s.avg),
                    backgroundColor: 'rgba(244, 67, 54, 0.25)',
                    borderColor: '#D32F2F',
                    borderWidth: 2,
                }],
            };
        },
        topRadar() {
            const list = this.sections?.top || [];
            return {
                labels: list.map((s) => s.name),
                datasets: [{
                    label: 'Promedio %',
                    data: list.map((s) => s.avg),
                    backgroundColor: 'rgba(76, 175, 80, 0.25)',
                    borderColor: '#388E3C',
                    borderWidth: 2,
                }],
            };
        },
        flagsRadar() {
            const list = this.topFlags?.topFlags || [];
            return {
                labels: list.map((f) => f.code),
                datasets: [{
                    label: 'Promedio %',
                    data: list.map((f) => f.pct),
                    backgroundColor: 'rgba(244, 67, 54, 0.25)',
                    borderColor: '#D32F2F',
                    borderWidth: 2,
                }],
            };
        },
    },
    async mounted() {
        await this.loadAll();
    },
    methods: {
        ...mapMutations('notification', ['show']),
        colorForLevel(level) {
            return { Crítico: 'red', Básico: 'orange', Aceptable: 'amber', Avanzado: 'green', Excelente: 'blue' }[level] || 'grey';
        },
        async loadAll() {
            this.loading = true;
            try {
                const [o, r, s, f, t, resp] = await Promise.all([
                    getDashboardOverview(),
                    getDashboardByRubro(),
                    getDashboardBySection(),
                    getDashboardTopFlags(),
                    getDashboardTrend(),
                    getDashboardResponses({ page: 1, limit: 20 }),
                ]);
                this.overview = o;
                this.rubros = r;
                this.sections = s;
                this.topFlags = f;
                this.trend = t;
                this.responses = resp;
            } catch (e) {
                this.show({
                    text: e?.response?.data?.error?.message || e?.response?.data?.message || e.message || 'No se pudo cargar el dashboard',
                    color: 'error',
                });
            } finally {
                this.loading = false;
            }
        },
        async onRespPage({ page, itemsPerPage }) {
            try {
                this.responses = await getDashboardResponses({ page, limit: itemsPerPage });
            } catch (e) {
                this.show({
                    text: e?.response?.data?.error?.message || e?.response?.data?.message || e.message || 'No se pudieron cargar los intentos',
                    color: 'error',
                });
            }
        },
    },
};
</script>

<style scoped>
.tabs-nav {
  display: flex;
  gap: 4px;
  border-bottom: 2px solid #e0e0e0;
  overflow-x: auto;
  background: #fff;
}
.tab-btn {
  background: transparent;
  border: none;
  border-bottom: 3px solid transparent;
  padding: 10px 14px;
  cursor: pointer;
  font-size: 0.9rem;
  font-weight: 500;
  color: #555;
  white-space: nowrap;
  margin-bottom: -2px;
  transition: color 0.15s, border-color 0.15s;
}
.tab-btn:hover {
  color: #1976d2;
}
.tab-btn.active {
  color: #1976d2;
  border-bottom-color: #1976d2;
}
.tab-content {
  min-height: 200px;
}
</style>
